import {
  GetCraftsmenIndustryGql,
  GetCraftsmenRegionDirectoryPageGql,
  GetCraftsmenRegionDirectoryRegionsGql,
  GetCraftsmenRegionGql,
  GetCraftsmenRegionPageGql,
  GetIndustryRegionPageGql,
  SearchTopicOverviewPagesGql,
  SiteLocale,
} from '../../../../__generated__/datocms.types'
import {Injectable} from '@angular/core'
import {
  parseCraftsmenRegionPage,
  parseIndustryRegionPage,
  parseRegion,
  parseRegionalIndustry,
  parseRegionDirectoryPage,
  parseRegionWithExcludedIndustries,
  parseSideNavigationIndustryOverviewPageItem,
} from '../serde/craftsmen-region-page.serde'
import {CraftsmenRegionPageResolved} from '../../../pages/public/craftsmen-region-directory/region-page/craftsmen-region-page.resolver'
import {CMS_GRAPHQL_CLIENT_NAME} from '../content-graphql-split'
import {IndustryRegionPageResolved} from '../../../pages/public/craftsmen-region-directory/industry-region-page/industry-region-page.resolver'
import {TranslocoService} from '@ngneat/transloco'
import {getSiteLocale} from '../site-locale'
import {sanitizeSlug} from '../sanitize-slug'
import {ReplacePropsPipe} from '../../../shared/pipes/replace-props.pipe'
import {
  CraftsmenRegionDirectoryPage,
  Region,
  RegionalIndustry,
  RegionWithExcludedIndustries,
} from '../types/craftsmen-region-types'
import {pickProperty} from '../../graphql/graphql-utils'
import {LinkService} from '../../i18n/link.service'
import {map, mergeMap} from 'rxjs/operators'
import {Observable, of, zip} from 'rxjs'

@Injectable()
export class CraftsmenRegionPagesContentService {
  constructor(
    private readonly getRegionGql: GetCraftsmenRegionGql,
    private readonly getIndustryGql: GetCraftsmenIndustryGql,
    private readonly getCraftsmenRegionPageGql: GetCraftsmenRegionPageGql,
    private readonly getIndustryRegionPageGql: GetIndustryRegionPageGql,
    private readonly translationService: TranslocoService,
    private readonly getCraftsmenRegionDirectoryPageGql: GetCraftsmenRegionDirectoryPageGql,
    private readonly getCraftsmenRegionDirectoryRegionsGql: GetCraftsmenRegionDirectoryRegionsGql,
    private readonly searchTopicOverviewPagesGql: SearchTopicOverviewPagesGql,
    private readonly linkService: LinkService,
  ) {}

  getCraftsmenRegionDirectoryPage(regionPageSize: number): CraftsmenRegionDirectoryPage {
    const locale = getSiteLocale(this.translationService)
    const pageObservable = this.getCraftsmenRegionDirectoryPageGql
      .fetch({locale, regionPageSize}, {context: {clientName: CMS_GRAPHQL_CLIENT_NAME}})
      .pipe(
        map(r => pickProperty(r, 'data')),
        map(data => {
          if (!data.page) throw new Error('No page could be fetched from the cms')
          const {content, excludedRegionalIndustries} = parseRegionDirectoryPage(data.page)
          const industries = data.industries.map(i => parseRegionalIndustry(i))
          const excludedRegionalIndustriesSlugs = excludedRegionalIndustries.map(ri => ri.slug)
          return {
            content,
            regions: data.regions.map(r => parseRegionWithExcludedIndustries(r)),
            regionsTotal: data.regionsMeta.count,
            topRegions: data.page.topRegions.map(r => parseRegion(r)),
            industries: industries
              .filter(ri => !!ri.slug.length)
              .filter(ri => !ri.hideInGeneratedPages)
              .filter(ri => !excludedRegionalIndustriesSlugs.includes(ri.slug)),
            sideNavigationIndustries: parseSideNavigationIndustryOverviewPageItem(data.sideNavIndustries).filter(
              sni => !!sni.slug.length && !!sni.title.length,
            ),
          }
        }),
      )

    return {
      regions$: pageObservable.pipe(map(page => ({value: page.regions, total: page.regionsTotal}))),
      topRegions$: pageObservable.pipe(map(page => page.topRegions)),
      industries$: pageObservable.pipe(map(page => page.industries)),
      content$: pageObservable.pipe(map(page => page.content)),
      sideNavigationIndustries$: pageObservable.pipe(map(page => page.sideNavigationIndustries)),
    }
  }

  getCraftsmenRegionDirectoryRegions(page: {
    size: number
    offset: number
  }): Observable<{total: number; value: RegionWithExcludedIndustries[]}> {
    const locale = getSiteLocale(this.translationService)
    return this.getCraftsmenRegionDirectoryRegionsGql
      .fetch({locale, pageSize: page.size, offset: page.offset}, {context: {clientName: CMS_GRAPHQL_CLIENT_NAME}})
      .pipe(
        map(r => pickProperty(r, 'data')),
        map(data => {
          return {
            total: data.regionsMeta.count,
            value: data.regions.map(r => parseRegionWithExcludedIndustries(r)),
          }
        }),
      )
  }

  getRegion(regionSlug: string): Observable<Region | undefined> {
    const locale = getSiteLocale(this.translationService)
    return this.getRegionGql
      .fetch({regionSlug: sanitizeSlug(regionSlug), locale}, {context: {clientName: CMS_GRAPHQL_CLIENT_NAME}})
      .pipe(
        map(({data: {region}}) => {
          if (!region) {
            return undefined
          }

          return parseRegion(region)
        }),
      )
  }

  private getIndustry(locale: SiteLocale, industrySlug: string): Observable<RegionalIndustry | undefined> {
    return this.getIndustryGql
      .fetch({industrySlug: sanitizeSlug(industrySlug), locale}, {context: {clientName: CMS_GRAPHQL_CLIENT_NAME}})
      .pipe(
        map(({data: {industry}}) => {
          if (!industry) {
            return undefined
          }

          const parsed = parseRegionalIndustry(industry)

          if (parsed.hideInGeneratedPages) {
            return undefined
          }

          return parsed
        }),
      )
  }

  getCraftsmenRegionPageAndRegion(regionSlug: string): Observable<CraftsmenRegionPageResolved> {
    const locale = getSiteLocale(this.translationService)
    return this.getRegion(regionSlug).pipe(
      mergeMap(region => {
        if (!region) {
          throw RangeError(`No Region for slug found: ${regionSlug}`)
        }

        const result = this.getCraftsmenRegionPageGql.fetch(
          {regionId: region.cmsId, locale},
          {context: {clientName: CMS_GRAPHQL_CLIENT_NAME}},
        )
        return zip(of(region), result)
      }),
      map(([region, result]) => ({region, ...result.data})),
      map(({region, page, defaultPage}) => {
        const pageData = page ?? defaultPage

        if (!pageData) {
          throw RangeError(`No default page not found for ${regionSlug}`)
        }

        const {cover, content, linkedIndustries, seo} = parseCraftsmenRegionPage(this.linkService, pageData)

        return {
          seo,
          region,
          cover,
          linkedIndustries: linkedIndustries.filter(i => !i.hideInGeneratedPages),
          content,
        }
      }),
    )
  }

  getIndustryRegionPage(regionSlug: string, industrySlug: string): Observable<IndustryRegionPageResolved> {
    const locale = getSiteLocale(this.translationService)
    return zip(this.getRegion(regionSlug), this.getIndustry(locale, industrySlug)).pipe(
      mergeMap(([region, industry]) => {
        if (!region) {
          throw RangeError(`No Region for slug found: ${regionSlug}`)
        }

        if (!industry) {
          throw RangeError(`No Industry not found for slug: ${industrySlug}`)
        }

        const result = this.getIndustryRegionPageGql.fetch(
          {regionId: region.cmsId, industryId: industry.cmsId, locale},
          {context: {clientName: CMS_GRAPHQL_CLIENT_NAME}},
        )
        return zip(of(region), of(industry), result)
      }),
      map(([region, industry, result]) => ({region, industry, ...result.data})),
      mergeMap(({region, industry, page, defaultPage, relatedIndustries}) => {
        const pageData = page ?? defaultPage

        if (pageData?.content.map(c => c.__typename).includes('FieldRelatedArticlesSectionRecord')) {
          const notUndefined = <TValue>(value: TValue | undefined): value is TValue => !!value
          return this.searchTopicOverviewPagesGql
            .fetch({industryId: industry.cmsId, locale}, {context: {clientName: CMS_GRAPHQL_CLIENT_NAME}})
            .pipe(
              map(result => result.data.allTopicOverviewPageModels.map(p => p.slug)),
              map(topics => topics.filter(notUndefined)),
              map(relatedTopics => {
                return {
                  region,
                  industry,
                  page,
                  defaultPage,
                  relatedIndustries,
                  relatedTopics,
                }
              }),
            )
        }

        return of({region, industry, page, defaultPage, relatedIndustries, relatedTopics: []})
      }),
      map(({region, industry, page, defaultPage, relatedIndustries, relatedTopics}) => {
        const pageData = page ?? defaultPage

        if (!pageData) {
          throw RangeError(`No default page not found for ${regionSlug} and industry: ${industrySlug}`)
        }

        const relatedIndustriesByRegion = relatedIndustries.find(r => r.region?.slug === regionSlug)
        const defaultRelatedIndustries = relatedIndustries.find(r => !r.region)
        const relevantRelatedIndustries =
          relatedIndustriesByRegion?.relatedIndustries ?? defaultRelatedIndustries?.relatedIndustries ?? []

        const replacePropsPipe = new ReplacePropsPipe()
        const props = {
          industryName: industry.label,
          regionName: region.regionName,
          industryTextVariable: industry.textVariable || industry.label,
        }

        const {cover, content, seo} = parseIndustryRegionPage(
          this.linkService,
          pageData,
          relevantRelatedIndustries,
          relatedTopics,
          value => replacePropsPipe.transform(value, props),
        )

        return {
          seo,
          region,
          industry,
          content,
          cover: {
            title: replacePropsPipe.transform(cover.title, props),
            subtitle: replacePropsPipe.transform(cover.subtitle, props),
          },
        }
      }),
    )
  }
}
